import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Scale, Lock } from 'lucide-react';
import BilanBilateralTable from '../components/BilanBilateralTable';
import DownloadFinancePdfButton from '../components/DownloadFinancePdfButton';

const fmt = (n) => (parseFloat(n) || 0).toLocaleString('fr-FR');

const MONTH_NAMES = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

export default function BilanInvestisseur({ user, onLogout }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const today = new Date();
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1); // 1..12
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/financial/bilan`);
        setItems(res.data?.items || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Filtre par mois : on garde les items dont la date est antérieure ou égale à la fin du mois choisi
  const filteredItems = useMemo(() => {
    const endOfMonth = new Date(selectedYear, selectedMonth, 0, 23, 59, 59); // dernier jour du mois
    return items.filter((i) => {
      const d = i.date || i.created_at;
      if (!d) return true; // si pas de date, on l'inclut (snapshot courant)
      try {
        return new Date(d) <= endOfMonth;
      } catch {
        return true;
      }
    });
  }, [items, selectedMonth, selectedYear]);

  const actifs = filteredItems.filter((i) => i.type === 'actif');
  const passifs = filteredItems.filter((i) => i.type === 'passif');
  const totalActif = actifs.reduce((s, i) => s + (parseFloat(i.montant) || 0), 0);
  const totalPassif = passifs.reduce((s, i) => s + (parseFloat(i.montant) || 0), 0);
  const ecart = totalActif - totalPassif;
  const equilibre = Math.abs(ecart) < 0.01;

  const years = [];
  for (let y = today.getFullYear() - 2; y <= today.getFullYear() + 1; y++) years.push(y);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="bilan-investisseur">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate(-1)}
              variant="outline"
              className="gap-2"
              data-testid="bilan-inv-back-btn"
            >
              <ArrowLeft className="h-4 w-4" /> Retour
            </Button>
            <DownloadFinancePdfButton
              type="bilan"
              dateFin={(() => {
                const d = new Date(selectedYear, selectedMonth, 0);
                return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
              })()}
              testId="bilan-inv-download-pdf"
            />
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                <Scale className="h-7 w-7 text-indigo-600" /> Bilan Comptable
              </h1>
              <p className="text-gray-600 mt-1 text-sm">
                Au {MONTH_NAMES[selectedMonth - 1]} {selectedYear}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Label className="text-xs">Mois</Label>
              <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(parseInt(v, 10))}>
                <SelectTrigger className="w-36" data-testid="bilan-month-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTH_NAMES.map((m, idx) => (
                    <SelectItem key={idx} value={String(idx + 1)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs">Année</Label>
              <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v, 10))}>
                <SelectTrigger className="w-24" data-testid="bilan-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Badge variant="outline" className="gap-1 border-gray-300 text-gray-600 bg-gray-50">
              <Lock className="h-3 w-3" /> Lecture seule
            </Badge>
          </div>
        </div>

        {/* Résumé 3 cartes */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card
            onClick={() => navigate('/bilan/actif')}
            className="border-l-4 border-l-blue-500 bg-blue-50 cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all"
            data-testid="card-total-actif"
          >
            <CardContent className="p-4">
              <p className="text-xs text-blue-700 font-semibold uppercase">Total Actif →</p>
              <p className="text-2xl font-bold text-blue-800">{fmt(totalActif)} FCFA</p>
              <p className="text-xs text-gray-600 mt-1">{actifs.length} ligne(s) · Voir détail</p>
            </CardContent>
          </Card>
          <Card
            onClick={() => navigate('/bilan/passif')}
            className="border-l-4 border-l-amber-500 bg-amber-50 cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all"
            data-testid="card-total-passif"
          >
            <CardContent className="p-4">
              <p className="text-xs text-amber-700 font-semibold uppercase">Total Passif →</p>
              <p className="text-2xl font-bold text-amber-800">{fmt(totalPassif)} FCFA</p>
              <p className="text-xs text-gray-600 mt-1">{passifs.length} ligne(s) · Voir détail</p>
            </CardContent>
          </Card>
          <Card className={`border-l-4 ${equilibre ? 'border-l-green-500 bg-green-50' : 'border-l-red-500 bg-red-50'}`}>
            <CardContent className="p-4">
              <p className={`text-xs font-semibold uppercase ${equilibre ? 'text-green-700' : 'text-red-700'}`}>
                {equilibre ? 'Bilan Équilibré' : 'Écart'}
              </p>
              <p className={`text-2xl font-bold ${equilibre ? 'text-green-800' : 'text-red-800'}`}>
                {equilibre ? '✓ 0' : (ecart > 0 ? '+' : '') + fmt(ecart)} FCFA
              </p>
              <p className="text-xs text-gray-600 mt-1">Actif − Passif</p>
            </CardContent>
          </Card>
        </div>

        {/* Tableau Bilan bilatéral (filtré selon le mois sélectionné) */}
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <BilanBilateralTable items={filteredItems} />
        )}
      </div>
    </Layout>
  );
}
